import { useEffect } from "react";
import { noop } from "lodash";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useNearMeFilter } from "@/react/components/shared/MemberFilters/SharedFilters/NearMe/useNearMeFilter";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { useSmOrMdScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { usePunditUserContext } from "@circle-react/contexts";
import type { Space } from "@circle-react/types";
import { EmptyState } from "@circle-react-uikit/EmptyState";
import type { BaseFilter } from "../../MemberFilters/types";
import { useMemberFilterUrl } from "../../MemberFilters/useMemberFilterUrlV2";
import { ProgressiveImage } from "../../ProgressiveImage";
import type { SortOptions, Views } from "../constants";
import { SORT_OPTIONS, VIEWS } from "../constants";
import { CardView } from "./CardView/CardViewV2";
import { CommunityMembersV2Skeleton } from "./CommunityMembersV2Skeleton";
import { ListView } from "./ListView/ListViewV2";
import { useSelectMembersInfiniteQueryV2 } from "./hooks/useSelectMembersInfiniteQueryV2";
import emptyMembers1x from "./images/emptyMembers.png";
import emptyMembers2x from "./images/emptyMembers@2x.png";
import emptyMembers3x from "./images/emptyMembers@3x.png";
import "./styles.scss";

const emptyMembersImageVariants = {
  thumbnail: emptyMembers1x,
  small: emptyMembers2x,
  medium: emptyMembers3x,
  large: emptyMembers3x,
};

export interface CommunityMembersProps {
  activeView?: Views;
  baseFilters?: BaseFilter[];
  defaultSort?: SortOptions;
  setMemberCount?: (memberCount: number) => void;
  shouldShowHiddenMemberTooltip: boolean;
  space?: Space;
}

export const CommunityMembers = ({
  setMemberCount = noop,
  defaultSort = SORT_OPTIONS[0],
  activeView,
  baseFilters,
  ...rest
}: CommunityMembersProps) => {
  const history = useHistory();
  const { currentCommunityMember } = usePunditUserContext();
  const isMobile = useSmOrMdScreenMediaQuery();

  const memberDataParams = useSelectMembersInfiniteQueryV2({
    defaultSort,
    baseFilters: baseFilters ?? [],
    isAdminScope: isCommunityAdmin(currentCommunityMember),
  });

  const { isLoading, dataLength, totalMembers } = memberDataParams;
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const { nearMeFilter, isNearMeActive } = useNearMeFilter();
  const { removeFilterFromUrl } = useMemberFilterUrl();

  useEffect(() => {
    if (dataLength) {
      setMemberCount(dataLength);
    }
  }, [dataLength, setMemberCount]);

  if (isLoading) {
    return (
      <CommunityMembersV2Skeleton
        view={isMobile ? VIEWS.LIST : (activeView ?? VIEWS.LIST)}
      />
    );
  }

  if (totalMembers <= 1 && isAdmin) {
    return (
      <EmptyState
        header={t("member_directory.no_members.title")}
        description={
          isNearMeActive
            ? t("members_directory.no_members.description_with_location", {
                location: nearMeFilter?.value?.name,
              })
            : t("member_directory.no_members.description")
        }
        primaryLabel={t("member_directory.no_members.cta")}
        onPrimaryClick={() => {
          history.push("/audience/manage");
        }}
      />
    );
  }

  if (dataLength === 0) {
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="mt-14 flex flex-1 items-center justify-center">
          <ProgressiveImage
            className="h-36"
            src={emptyMembersImageVariants.large}
            srcVariants={emptyMembersImageVariants}
            alt={t("members_directory.no_members.title")}
          />
        </div>
        <EmptyState
          header={t("members_directory.no_members.title")}
          description={
            isNearMeActive
              ? t("members_directory.no_members.description_with_location", {
                  location: nearMeFilter?.value?.name,
                })
              : t("members_directory.no_members.description")
          }
          secondaryLabel={t("members_directory.no_members.view_all_members")}
          onSecondaryClick={removeFilterFromUrl}
        />
      </div>
    );
  }

  const defaultView = activeView === VIEWS.CARDS ? CardView : ListView;
  const ActiveViewComponent = isMobile ? ListView : defaultView;

  return (
    <div className="px-0">
      <ActiveViewComponent memberDataParams={memberDataParams} {...rest} />
    </div>
  );
};
